import { Button, TextField } from '@mgh-app/component-library';
import { ValidationErrors } from 'components/ValidationErrors';
import { MainLayoutContext } from 'layouts/MainLayout';
import { useAppNavigation } from 'navigation/useAppNavigation';
import { useContext, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { UpdateEmailAddressError, useLazyUpdateEmailAddressQuery } from 'services/UsersService';
import { display, flexDirection, margin, twCls } from 'style';
import { EmailUtils } from 'utils/EmailUtils';
import { localStorageService } from 'services/localStorageService';

interface Fields {
  email: string;
}

type ErrorTypes = 'required' | 'pattern';

const ErrorHelper = {
  email: {
    required: 'Email address is required',
    pattern: 'Email address is incorrect',
    validate: 'Email address should be different from the current one',
  },
};

interface EmailChangeFormProps {
  email: string | null;
}

const EmailChangeForm = ({ email }: EmailChangeFormProps): JSX.Element => {
  const [customError, setCustomError] = useState<string[]>();
  const { handleSubmit, control, formState } = useForm<Fields>({
    defaultValues: {
      email: email ?? '',
    },
  });

  const [updateEmail] = useLazyUpdateEmailAddressQuery();
  const { navigateVerifyEmailChange } = useAppNavigation();

  const sendVerificationCode = async ({ email }: Fields) => {
    setCustomError([]);
    try {
      await updateEmail({ email }).unwrap();
      navigateVerifyEmailChange();
    } catch (err) {
      const error = err as UpdateEmailAddressError;
      if (error.message) {
        if (Array.isArray(error?.message)) {
          setCustomError(error.message);
        } else if (error?.message) {
          setCustomError([error.message]);
        } else {
          setCustomError(['Unknown error']);
        }
      } else {
        console.error(err);
      }
    }
  };

  return (
    <>
      {email && (
        <>
          <p>
            Provide new email address for your account and we will send you a verification code.
          </p>
          <Controller
            name="email"
            control={control}
            rules={{
              required: true,
              pattern: EmailUtils.EmailRegex,
              validate: (value) => value !== email,
            }}
            render={({ field, fieldState: { error } }) => (
              <TextField
                error={error !== undefined}
                helperText={ErrorHelper.email[error?.type as ErrorTypes]}
                className={twCls(margin('mt-11'))}
                label="Email Address"
                {...field}
              />
            )}
          />
          <ValidationErrors className={twCls(margin('mt-10'))} errors={customError} />
          <Button
            className={twCls(margin('mt-4'))}
            onClick={handleSubmit(sendVerificationCode)}
            disabled={!formState.isDirty || formState.isSubmitting}
          >
            Continue
          </Button>
        </>
      )}
    </>
  );
};

export const EmailChange = (): JSX.Element => {
  const { setTitle, showNavigationButtons, showUserProfile, navigateHomeAction, showHomeButton } =
    useContext(MainLayoutContext);

  useEffect(() => {
    setTitle('Change Email');
    showNavigationButtons(true);
    showUserProfile(true);
    showHomeButton(true);
  }, [navigateHomeAction, setTitle, showHomeButton, showNavigationButtons, showUserProfile]);

  return (
    <div className={twCls(display('flex'), flexDirection('flex-col'), margin('mt-20'))}>
      <EmailChangeForm email={localStorageService.getUser()} />
    </div>
  );
};
